import api from '@/services/api';

const smrPath = '/smr';

export interface ProductFeeBand {
    band_index: number;
    cost_net: string | null;
    percentage: string | null;
}

export interface RefLabel {
    id: string;
    code: string;
    name: string;
}

export interface MoneyValue {
    amount: string;
    amount_raw: string;
    currency: string;
    minor: number;
}

export interface ProductFeeRecord {
    id: string;
    product_id: string;
    fee_id: string;
    fee: {
        id: string;
        name: string;
        transaction_type: (RefLabel & { numeric_ref: number | null }) | null;
    };
    value_type: RefLabel | null;
    scheduling_method: RefLabel | null;
    payer_pair_overridden: boolean;
    paid_by: RefLabel | null;
    paid_to: RefLabel | null;
    vat_category: RefLabel | null;
    applies_to: RefLabel[];
    application_scope: 'per_plan' | 'per_service';
    is_absorbed: boolean;
    cap_value_gross_money: MoneyValue | null;
    default_cost_net_money: MoneyValue | null;
    default_cost_gross_money: MoneyValue | null;
    default_percentage: string | null;
    display_order: number;
    bands: {
        band_index: number;
        cost_net_money: MoneyValue | null;
        cost_gross_money: MoneyValue | null;
        percentage: string | null;
    }[];
    created_at: string;
    updated_at: string;
}

export interface ProductFeePayload {
    fee_id: string;
    value_type_id: string;
    scheduling_method_id: string;
    paid_by_id: string;
    paid_to_id: string;
    vat_category_id: string;
    applies_to_ids: string[];
    application_scope: 'per_plan' | 'per_service';
    is_absorbed: boolean;
    cap_value_gross: string | null;
    default_cost_net: string | null;
    default_percentage: string | null;
    display_order?: number;
    bands?: ProductFeeBand[];
}

export interface FeeReferenceData {
    transaction_types: (RefLabel & { numeric_ref: number | null;
        is_fee_eligible: boolean;
        paid_by_id?: string | null;
        paid_to_id?: string | null;
    })[];
    fee_value_types: (RefLabel & { is_percentage: boolean; requires_lookup: boolean })[];
    fee_scheduling_methods: RefLabel[];
    vat_categories: (RefLabel & { rate_percent: string })[];
    fee_targets: RefLabel[];
    quote_types: RefLabel[];
}

export const fetchProductFees = async (productId: string): Promise<ProductFeeRecord[]> =>
    (await api.get(`${smrPath}/products/${productId}/fees`)).data.data ?? [];

export const fetchProductFee = async (productId: string, productFeeId: string): Promise<ProductFeeRecord> =>
    (await api.get(`${smrPath}/products/${productId}/fees/${productFeeId}`)).data.data;

export const createProductFee = async (productId: string, payload: ProductFeePayload): Promise<ProductFeeRecord> =>
    (await api.post(`${smrPath}/products/${productId}/fees`, payload)).data.data;

export const updateProductFee = async (
    productId: string,
    productFeeId: string,
    payload: Partial<ProductFeePayload>,
): Promise<ProductFeeRecord> =>
    (await api.put(`${smrPath}/products/${productId}/fees/${productFeeId}`, payload)).data.data;

export const deleteProductFee = async (productId: string, productFeeId: string): Promise<void> => {
    await api.delete(`${smrPath}/products/${productId}/fees/${productFeeId}`);
};

export interface PayerPairOverridePayload {
    paid_by_id: string;
    paid_to_id: string;
}

export const setProductFeePayerPairOverride = async (
    productId: string,
    productFeeId: string,
    payload: PayerPairOverridePayload,
): Promise<ProductFeeRecord> =>
    (await api.put(`${smrPath}/products/${productId}/fees/${productFeeId}/payer-pair-override`, payload)).data.data;

export const clearProductFeePayerPairOverride = async (
    productId: string,
    productFeeId: string,
): Promise<ProductFeeRecord> =>
    (await api.delete(`${smrPath}/products/${productId}/fees/${productFeeId}/payer-pair-override`)).data.data;

export const fetchFeeReferenceData = async (): Promise<FeeReferenceData> =>
    (await api.get(`${smrPath}/fee-reference-data`)).data.data;
